import { MapPin, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface Neighborhood {
  id: string;
  name: string;
  subheading: string;
  paragraph: string;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
}
const neighborhoods: Neighborhood[] = [{
  id: "brookhaven",
  name: "Brookhaven",
  subheading: "Historic charm, established tree lines, and classic residential homes",
  paragraph: "Brookhaven features a thoughtful blend of mid-century residences, graceful brick traditional homes, and contemporary updates. As our home base, we take immense pride in working closely with local homeowners to preserve historic architectural integrity while modernizing interior floor plans, reconfiguring kitchen layouts, and expanding family living spaces for today's lifestyle.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_residential_exterior.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_residential_exterior.png-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
    alt: "Renovated traditional brick home with landscaping in Brookhaven Georgia"
  }
}, {
  id: "dekalb-county",
  name: "DeKalb County",
  subheading: "Regional community connections and diverse residential architecture",
  paragraph: "Across unincorporated DeKalb County and neighboring perimeter communities, residential styles span from mid-century ranch layouts to sprawling multi-story homes. We serve this wider district because homeowners value dependable craftsmanship, clear communication, and seamless coordination through local municipal permitting and building standards.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/dekalb_county_home_renovation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/dekalb_county_home_renovation.png-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
    alt: "Spacious suburban home in DeKalb County with updated exterior accents"
  }
}, {
  id: "ashford-park",
  name: "Ashford Park",
  subheading: "Vibrant family enclave surrounding a beloved neighborhood park",
  paragraph: "Ashford Park is recognized for its welcoming sidewalks, neighborhood park gatherings, and a charming mix of updated bungalows alongside custom new builds. We concentrate renovation efforts here because active households frequently need expanded kitchens with central gathering islands, functional mudrooms, and smart primary suite expansions tailored to family life.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/ashford_park_kitchen_remodel.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/ashford_park_kitchen_remodel.webp-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
    alt: "Sunlit open kitchen renovation in an Ashford Park home"
  }
}, {
  id: "brookhaven-heights",
  name: "Brookhaven Heights",
  subheading: "Walkable side streets and character-rich residential parcels",
  paragraph: "Brookhaven Heights blends scenic, undulating streets with close proximity to neighborhood dining and transit. The neighborhood's character stems from its compact footprints and expressive architecture. We love working here to help homeowners optimize existing square footage through luxury bathroom transformations, finished lower levels, and thoughtful indoor-outdoor transitions.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_heights_bathroom.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_heights_bathroom.webp-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
    alt: "Luxury tiled bathroom with double vanity in Brookhaven Heights"
  }
}, {
  id: "drew-valley",
  name: "Drew Valley",
  subheading: "Generous wooded lots, original ranches, and seamless modernizations",
  paragraph: "Drew Valley is cherished for its generous lot dimensions and quiet, tree-covered blocks lined with classic 1950s ranches. Many property owners choose to stay rooted in the neighborhood while transforming compartmentalized rooms into open-concept plans, adding second-story additions, or constructing sun-drenched family rooms that look out onto private backyards.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/drew_valley_living_addition.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/drew_valley_living_addition.png-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
    alt: "Open-concept living room addition with hardwood flooring in Drew Valley"
  }
}, {
  id: "lynwood-park",
  name: "Lynwood Park",
  subheading: "Rich community heritage and an exciting residential evolution",
  paragraph: "Lynwood Park carries deep historical roots and an authentic neighborhood spirit anchored by its dedicated community park and recreation facilities. We are committed to serving Lynwood Park by providing high-quality craftsmanship, respectful exterior renovations, and tailored interior redesigns that celebrate both past heritage and present homeowners.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/lynwood_park_exterior_patio.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/lynwood_park_exterior_patio.png-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
    alt: "Custom stone patio and covered outdoor space in Lynwood Park"
  }
}, {
  id: "sexton-woods",
  name: "Sexton Woods",
  subheading: "Peaceful neighborhood atmosphere bridging Brookhaven and Chamblee",
  paragraph: "Sexton Woods offers a peaceful woodland setting with low-traffic residential streets and a harmonious mix of renovated mid-century homes and substantial new construction. Homeowners often engage us to rework older basements into vibrant entertainment hubs, install bespoke cabinetry, and create durable, elegant spaces built to withstand decades of family life.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/sexton_woods_basement_renovation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/sexton_woods_basement_renovation.png-wvc-srcset",
    sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
    alt: "Finished entertainment basement with custom built-ins in Sexton Woods"
  }
}];
export default function NeighborhoodServiceAreas() {
  return <section id="neighborhoodserviceareas" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border/60">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 text-primary font-medium text-sm mb-3">
            <MapPin className="size-4 text-primary" aria-hidden="true" />
            <span>Local Neighborhood Service Areas</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-foreground">
            Where We Build in Brookhaven and Beyond
          </h2>
          <p className="mt-4 text-base sm:text-lg text-foreground/80 leading-relaxed font-default">
            Every neighborhood in our community carries its own distinctive architectural rhythm, lot typography,
            and heritage. Learn how we tailor each residential renovation to respect local neighborhood context.
          </p>
        </div>

        {/* Neighborhood Grid: 7 items */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {neighborhoods.map((neighborhood, index) => <Card key={neighborhood.id} data-index={index} className={`bg-card text-card-foreground border border-border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col ${index === 0 ? "lg:col-span-2" : ""}`}>
              <div className={`relative overflow-hidden bg-muted ${index === 0 ? "h-64 sm:h-80" : "h-56 sm:h-64"}`}>
                <img src={neighborhood.image.src} data-wvc-srcset={neighborhood.image.srcset} data-wvc-sizes={neighborhood.image.sizes} alt={neighborhood.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover object-center transition-transform duration-300 hover:scale-102" />
                <div className="absolute top-3 left-3 bg-secondary text-secondary-foreground text-xs font-semibold px-2.5 py-1 rounded-md shadow-xs">
                  {neighborhood.name}
                </div>
              </div>

              <CardContent className="p-6 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="text-xl font-serif font-semibold text-foreground mb-1.5">
                    {neighborhood.name}
                  </h3>
                  <p className="text-xs font-medium uppercase tracking-wider text-primary mb-3">
                    {neighborhood.subheading}
                  </p>
                  <p className="text-sm text-foreground/80 leading-relaxed">
                    {neighborhood.paragraph}
                  </p>
                </div>
              </CardContent>
            </Card>)}
        </div>

        {/* Bottom Estimate Banner */}
        <div className="mt-12 sm:mt-16 bg-muted rounded-xl p-8 sm:p-10 border border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h3 className="text-2xl font-serif font-bold text-foreground">
              Planning a renovation in your neighborhood?
            </h3>
            <p className="mt-2 text-sm sm:text-base text-foreground/80">
              Speak with our design and construction team about your home in Brookhaven, Ashford Park, Drew Valley, or surrounding areas.
            </p>
          </div>
          <Button asChild size="lg" className="shrink-0 bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center gap-2">
              Request an Estimate
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}